'use client';

import {autoUpdate, useDismiss, useFloating, useHover, useInteractions} from '@floating-ui/react';
import {flip, offset, Placement} from '@floating-ui/react-dom';
import {Transition} from '@headlessui/react';
import React, {Fragment, useId, useImperativeHandle, useState} from 'react';
import {cn} from '@rocket-ui/tools';

export interface NewPopoverRef {
	isOpen: boolean;
	close: () => void;
	open: () => void;
}

export interface NewPopoverProps {
	handler?: React.ReactNode | ((isOpen: boolean, open: () => void) => React.ReactNode);
	toggleHandler?: React.ReactNode | ((isOpen: boolean, open: () => void) => React.ReactNode);
	children: React.ReactNode;
	placement?: Placement;
	offset?: number;
	triggerOnHover?: boolean;
	className?: string;
}

export const NewPopover = React.forwardRef<NewPopoverRef, NewPopoverProps>(function NewPopover(
	{
		className,
		handler,
		toggleHandler,
		children,
		placement = 'bottom-end',
		offset: offsetValue = 12,
		triggerOnHover = false,
	},
	ref,
) {
	const id = useId();
	const [isOpen, setIsOpen] = useState(false);

	useImperativeHandle(ref, () => ({isOpen, close: () => setIsOpen(false), open: () => setIsOpen(true)}), [
		isOpen,
		setIsOpen,
	]);

	const open = () => setIsOpen(true);
	const toggleOpener = () => setIsOpen((prevState) => !prevState);

	const {x, y, strategy, refs, context} = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement,
		strategy: 'fixed',
		middleware: [flip(), offset(offsetValue)],
		whileElementsMounted: autoUpdate,
	});

	const dismiss = useDismiss(context);
	const hover = useHover(context, {enabled: triggerOnHover});
	const {getReferenceProps, getFloatingProps} = useInteractions([hover, dismiss]);

	return (
		<>
			<div
				id={`popup-handler-${id}`}
				role={'button'}
				className={cn('flex w-full cursor-pointer', className)}
				onClick={toggleHandler ? toggleOpener : open}
				ref={refs.setReference}
				{...getReferenceProps()}
				aria-expanded={isOpen}
				aria-controls={`popup-panel-${id}`}
			>
				{typeof handler === 'function' ? handler(isOpen, open) : handler}
				{typeof toggleHandler === 'function' ? toggleHandler(isOpen, toggleOpener) : toggleHandler}
			</div>

			<Transition
				show={isOpen}
				as={Fragment}
				enter="transition ease-out duration-200"
				enterFrom="opacity-0 translate-y-1"
				enterTo="opacity-100 translate-y-0"
				leave="transition ease-in duration-150"
				leaveFrom="opacity-100 translate-y-0"
				leaveTo="opacity-0 translate-y-1"
			>
				<div
					id={`popup-panel-${id}`}
					className="absolute z-10 bg-black/[.02] border border-black/[.04] backdrop-blur-3xl shadow rounded-md"
					style={{
						position: strategy,
						left: `${x}px`,
						top: `${y}px`,
						minWidth: refs.domReference?.current?.scrollWidth,
					}}
					ref={refs.setFloating}
					{...getFloatingProps()}
				>
					{children}
				</div>
			</Transition>
		</>
	);
});
